/**
 * Lightweight Supabase REST client — replaces the full @supabase/supabase-js SDK.
 * The full SDK adds ~180 KB to the bundle for just 2 API calls.
 * This fetch-based wrapper does the same thing in ~1 KB.
 */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

const ALLOWED_TABLES = ['waitlist', 'news_posts'] as const;
type AllowedTable = (typeof ALLOWED_TABLES)[number];

function validateTable(table: string): asserts table is AllowedTable {
    if (!ALLOWED_TABLES.includes(table as AllowedTable)) {
        throw new Error(`Table "${table}" is not allowed`);
    }
}

const headers = {
    'apikey': SUPABASE_ANON_KEY,
    'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
    'Content-Type': 'application/json',
    'Prefer': 'return=minimal',
};

/**
 * Get the count of rows in a table via server-side RPC function.
 * Uses security-definer function so anon key never has SELECT access to rows.
 */
export async function getRowCount(table: string): Promise<number | null> {
    try {
        validateTable(table);
        const res = await fetch(
            `${SUPABASE_URL}/rest/v1/rpc/get_waitlist_count`,
            {
                method: 'POST',
                headers,
            }
        );
        if (!res.ok) return null;
        const count = await res.json();
        return typeof count === 'number' ? count : null;
    } catch {
        return null;
    }
}

export type NewsPost = {
    id: number;
    slug: string;
    title: string;
    excerpt: string | null;
    body: string | null;
    category: string | null;
    published_at: string;
    source_url: string | null;
    source_name: string | null;
    image_url: string | null;
};

/**
 * Fetch published news posts, newest first.
 */
export async function fetchNewsPosts(): Promise<NewsPost[]> {
    try {
        const res = await fetch(
            `${SUPABASE_URL}/rest/v1/news_posts?select=id,slug,title,excerpt,body,category,published_at,source_url,source_name,image_url&published=eq.true&order=published_at.desc`,
            {
                headers: {
                    apikey: SUPABASE_ANON_KEY,
                    Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
                },
            }
        );
        if (!res.ok) return [];
        const data = (await res.json()) as NewsPost[];
        return Array.isArray(data) ? data : [];
    } catch {
        return [];
    }
}

/**
 * Fetch a single news post by slug.
 */
export async function fetchNewsPostBySlug(slug: string): Promise<NewsPost | null> {
    try {
        const res = await fetch(
            `${SUPABASE_URL}/rest/v1/news_posts?select=id,slug,title,excerpt,body,category,published_at,source_url,source_name,image_url&published=eq.true&slug=eq.${encodeURIComponent(slug)}&limit=1`,
            {
                headers: {
                    apikey: SUPABASE_ANON_KEY,
                    Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
                },
            }
        );
        if (!res.ok) return null;
        const data = (await res.json()) as NewsPost[];
        return Array.isArray(data) && data.length > 0 ? data[0] : null;
    } catch {
        return null;
    }
}

/**
 * Insert a row into a table. Returns { error } if failed.
 */
export async function insertRow(
    table: string,
    data: Record<string, unknown>
): Promise<{ error: { code?: string; message: string } | null }> {
    try {
        validateTable(table);
        const res = await fetch(
            `${SUPABASE_URL}/rest/v1/${table}`,
            {
                method: 'POST',
                headers,
                body: JSON.stringify(data),
            }
        );

        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            return {
                error: {
                    code: err.code || String(res.status),
                    message: err.message || res.statusText,
                },
            };
        }

        return { error: null };
    } catch (e: unknown) {
        return {
            error: {
                message: e instanceof Error ? e.message : 'Network error',
            },
        };
    }
}
